import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmptyArray, isNotEmptyArray } from '@/utils/formatChecker';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination, Thumbs } from 'swiper/core';
import FunwooImage from '@/components/common/FunwooImage';

import 'swiper/swiper.min.css';
import 'swiper/components/pagination/pagination.min.css';
import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/thumbs/thumbs.min.css';
import Class from './gallery.module.scss';

import Flexbox from '@/components/common/Flexbox';
import { ImPlay3 } from 'react-icons/im';
import YouTube from 'react-youtube';
import classNames from 'classnames';
import { Modal, ModalContent, ModalOverlay, useDisclosure } from '@chakra-ui/react';
import { FaTimes } from 'react-icons/fa';
import ReactIcon from '@/src/components/common/ReactIcon';

export interface GallerySection {
  key: string;
  text?: string;
  icon?: React.ReactNode;
  images: Array<string>;
}

interface Props {
  imageSections: Array<GallerySection>;
  videos?: Array<string>;
}

SwiperCore.use([Pagination, Navigation, Thumbs]);

const Gallery: React.FC<Props> = ({ imageSections, videos = [], children }) => {
  const [swiper, setSwiper] = useState<SwiperCore>();
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore>();
  const [currentIndex, setCurrentIndex] = useState(0);

  const { isOpen: isZoomOpen, onOpen: onZoomOpen, onClose: onZoomClose } = useDisclosure();

  const thumbs = useMemo(() => ({ swiper: thumbsSwiper }), [thumbsSwiper]);

  const buttonData = useMemo<
    Array<{
      index: number;
      text?: string;
      icon?: React.ReactNode;
      key: string;
    }>
  >(() => {
    const data: Array<{
      index: number;
      text?: string;
      icon?: React.ReactNode;
      key: string;
    }> = [];
    imageSections.forEach((section, index, array) => {
      if (section.key === 'default') return;
      if (isEmptyArray(section.images)) return;
      const sectionIndex = array.slice(0, index).reduce((acc, cur) => acc + cur.images.length, 0);
      data.push({
        index: sectionIndex,
        text: section.text,
        icon: section.icon,
        key: section.key,
      });
    });
    if (isNotEmptyArray(videos)) {
      const videoSectionIndex = imageSections.reduce((acc, cur) => acc + cur.images.length, 0);
      data.push({
        index: videoSectionIndex,
        text: '影片',
        icon: <ReactIcon icon={ImPlay3} />,
        key: 'video',
      });
    }
    return data;
  }, [imageSections, videos]);

  const totalImages = useMemo(() => imageSections.reduce((acc, cur) => acc + cur.images.length, 0), [imageSections]);

  const slidesPerView = useMemo(() => {
    let defaultValue = 8;
    defaultValue -= buttonData.length;
    return Math.min(defaultValue, imageSections?.[0]?.images?.length);
  }, [buttonData, totalImages]);

  const slideTo = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      const index = parseInt(event.currentTarget.dataset.index ?? '0');
      thumbs.swiper?.slideTo(index);
      swiper?.slideTo(index);
    },
    [thumbs.swiper, swiper]
  );

  const onThumbSwipe = useCallback((swiper: SwiperCore) => setThumbsSwiper(swiper), [setThumbsSwiper]);

  const thumbPlaceholderCount = useMemo(() => {
    const buttonCount = buttonData.length;
    const requiredCount = 8;
    const count = requiredCount - slidesPerView - buttonCount;

    return count > 0 ? count : 0;
  }, [imageSections, buttonData, slidesPerView]);

  const zoomAssets = useMemo(() => {
    return imageSections
      .reduce(
        (acc, cur) => acc.concat(cur.images.map((image) => ({ src: image, alt: image, type: 'image' }))),
        [] as Array<ZoomAsset>
      )
      .concat(videos.map((video) => ({ src: video, alt: video, type: 'video' })));
  }, [imageSections, videos]);

  const onActiveIndexChange = useCallback((swiper: SwiperCore) => {
    setCurrentIndex(swiper.activeIndex);
  }, []);

  const onImageClick = useCallback(() => {
    onZoomOpen();
  }, []);

  return (
    <React.Fragment>
      <Zoom
        currentIndex={currentIndex}
        total={zoomAssets.length}
        assets={zoomAssets}
        isOpen={isZoomOpen}
        onClose={onZoomClose}
      />
      <div id={'gallery'} className={classNames(Class.gallery, 'relative w-full')}>
        <Swiper
          id={'main-swiper'}
          navigation
          thumbs={thumbs}
          className={classNames('relative sm:mb-px w-full h-auto flex', 'gallery')}
          slidesPerView={1}
          onSwiper={setSwiper}
          pagination={{
            dynamicBullets: true,
          }}
          onActiveIndexChange={onActiveIndexChange}
        >
          {imageSections.map((section) => (
            <React.Fragment key={section.key}>
              {section.images.map((image) => (
                <SwiperSlide className={'max-w-full'} key={image}>
                  <FunwooImage
                    src={image}
                    alt={image}
                    width={3}
                    height={2}
                    layout={'fill'}
                    className={'w-full h-full'}
                    onClick={onImageClick}
                  />
                </SwiperSlide>
              ))}
            </React.Fragment>
          ))}
          {isNotEmptyArray(videos) && (
            <SwiperSlide className={'max-w-full'}>
              {videos.map((video, index) => {
                const videoIndex = totalImages + index;
                const isFocused = videoIndex === currentIndex;
                return <Video key={video} src={video} isFocused={isFocused} />;
              })}
            </SwiperSlide>
          )}
        </Swiper>
        {isNotEmptyArray(buttonData) && (
          <Flexbox
            align={'center'}
            justify={'center'}
            className={classNames('sm:hidden sm:invisible text-sm text-black', 'pt-4 pb-2')}
          >
            {buttonData.map((button) => (
              <Flexbox
                type={'button'}
                as={'button'}
                align={'center'}
                key={button.key}
                onClick={slideTo}
                data-index={button.index}
                className={'mr-4 last:mr-0 py-1 px-4 h-8 border border-black rounded-full'}
              >
                <span className={'mr-2.5'}>{button.icon}</span>
                {button.text}
              </Flexbox>
            ))}
          </Flexbox>
        )}
        <div
          id={'thumb'}
          className={classNames(
            'hidden invisible sm:visible',
            'sm:flex flex-row',
            'w-full',
            'items-center justify-center overflow-hidden'
          )}
        >
          <Swiper
            id={'thumb-swiper'}
            navigation
            watchSlidesVisibility
            watchSlidesProgress
            className={'flex-1 flex-row z-10'}
            slidesPerView={slidesPerView}
            onSwiper={onThumbSwipe}
            spaceBetween={1}
          >
            {imageSections[0].images.map((image) => {
              return (
                <SwiperSlide
                  key={image}
                  className={'mr-px last:mr-0'}
                  style={{
                    width: `calc(100%/8 - 1px)`,
                  }}
                >
                  <FunwooImage
                    src={image}
                    alt={image}
                    width={1}
                    height={1}
                    objectFit={'cover'}
                    layout={'fill'}
                    className={'w-full h-auto'}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
          {Array.from({ length: thumbPlaceholderCount }).map((_, index) => (
            <div
              key={index}
              style={{
                width: `calc(100%/8 - 1px)`,
              }}
              className={classNames('relative', 'first:ml-0 ml-px', 'flex-shrink-0', 'bg-black bg-opacity-90')}
            >
              <span className={'block pt-[100%]'} />
            </div>
          ))}
          {buttonData.map((button) => (
            <Flexbox
              type={'button'}
              as={'button'}
              align={'center'}
              justify={'center'}
              key={button.key}
              onClick={slideTo}
              data-index={button.index}
              style={{
                width: `calc(100%/8 - 1px)`,
              }}
              className={classNames(
                'relative',
                'first:ml-0 ml-px',
                'text-sm text-white',
                'flex-shrink-0',
                'bg-black bg-opacity-90'
              )}
            >
              <span className={'block pt-[100%]'} />
              <span className={'absolute'}>{button.text}</span>
            </Flexbox>
          ))}
        </div>
        {children}
      </div>
    </React.Fragment>
  );
};

export default Gallery;

const Video: React.FC<{ src: string; isFocused?: boolean; onlyLockAspectOnMobile?: boolean; className?: string }> = ({
  src,
  isFocused = true,
  onlyLockAspectOnMobile = false,
  className,
}) => {
  const ref = React.useRef<YouTube>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (isFocused) return;
    ref.current.getInternalPlayer()?.pauseVideo?.();
  }, [isFocused]);

  const youtudeId = useMemo(() => {
    const url = new URL(src);
    return url.searchParams.get('v') ?? '';
  }, [src]);

  return (
    <div className={classNames('relative w-full', className)}>
      {
        <div
          className={classNames(onlyLockAspectOnMobile ? 'sm:hidden sm:invisible' : '', 'w-full pt-[calc(100%*2/3)]')}
        />
      }
      <div className={'absolute top-0 left-0 w-full h-full bg-gray50'}>
        <YouTube ref={ref} className={'w-full h-full'} videoId={youtudeId} containerClassName={'w-full h-full'} />
      </div>
    </div>
  );
};

interface ZoomAsset {
  src: string;
  alt: string;
  type: 'image' | 'video';
}

const Zoom: React.FC<{
  currentIndex: number;
  total: number;
  assets: Array<ZoomAsset>;
  isOpen: boolean;
  onClose: () => void;
}> = ({ currentIndex, total, assets, isOpen, onClose }) => {
  const [swiper, setSwiper] = useState<SwiperCore>();
  const [innerIndex, setInnerIndex] = useState(currentIndex);

  const modalContentRef = React.useRef<HTMLDivElement>(null);

  const { isOpen: isMouseMoving, onOpen: setMouseMoving, onClose: setMouseStop } = useDisclosure();
  const { isOpen: isActionAreaShow, onOpen: showActionArea, onClose: hideActionArea } = useDisclosure();

  useEffect(() => {
    setInnerIndex(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    if (!swiper) return;
    swiper.slideTo(innerIndex, 0);
  }, [innerIndex, swiper]);

  useEffect(() => {
    if (isMouseMoving) return showActionArea();
    const id = setTimeout(hideActionArea, 3000);

    return () => clearTimeout(id);
  }, [isMouseMoving]);

  const onActiveIndexChange = useCallback((swiper: SwiperCore) => {
    setInnerIndex(swiper.activeIndex);
  }, []);

  const onBlur = useCallback<React.FocusEventHandler<HTMLElement>>(
    (event) => {
      if (!isOpen) return;
      event.currentTarget.focus();
    },
    [isOpen]
  );

  const onKeyDown = useCallback<React.KeyboardEventHandler<HTMLElement>>(
    (event) => {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft') {
        if (swiper) {
          swiper.slidePrev();
        }
      } else if (event.key === 'ArrowRight') {
        if (swiper) {
          swiper.slideNext();
        }
      }
    },
    [isOpen, onClose, swiper]
  );

  const onMouseMove = useCallback<React.MouseEventHandler<HTMLElement>>(() => {
    if (!isOpen) return;
    setMouseMoving();
    setTimeout(setMouseStop, 1000);
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        ref={modalContentRef}
        margin={{ base: 0 }}
        minH={{ base: '100dh', sm: 'stretch' }}
        h={'full'}
        w={{ base: 'full' }}
        maxW={'none'}
        borderRadius={{ base: 0 }}
        containerProps={{
          w: 'full',
          minH: { base: '100dh', sm: '100vh' },
          h: 'full',
        }}
        onFocus={() => console.log('Modal focused')}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onMouseMove={onMouseMove}
      >
        <div
          className={classNames(
            'absolute top-0 inset-x-0',
            'z-10',
            'py-4 px-3',
            'flex items-center justify-end',
            'sm:bg-[#1C1E21] sm:bg-opacity-[40%]',
            isActionAreaShow ? 'sm:opacity-100' : 'sm:opacity-0'
          )}
        >
          <Flexbox
            as={'button'}
            align={'center'}
            justify={'center'}
            type={'button'}
            className={classNames('text-white text-lg', 'w-10 h-10', 'outline-none')}
            onClick={onClose}
          >
            <ReactIcon icon={FaTimes} className={classNames('sm:w-6 sm:h-6')} />
          </Flexbox>
        </div>
        <div className={classNames(Class.zoom, 'w-full h-full bg-gray900', 'flex items-center justify-center')}>
          <Swiper
            id={'zoom-swiper'}
            navigation
            className={classNames('relative', 'w-full h-screen flex', isActionAreaShow ? '' : 'inactive')}
            slidesPerView={1}
            onSwiper={setSwiper}
            onActiveIndexChange={onActiveIndexChange}
          >
            {assets.map((asset, index) => {
              const isFocused = innerIndex === index;
              return (
                <SwiperSlide key={asset.alt}>
                  {asset.type === 'image' ? (
                    <FunwooImage
                      src={asset.src}
                      alt={asset.alt}
                      width={3}
                      height={2}
                      layout={'fill'}
                      className={'w-full h-full'}
                      imageClassName={'object-contain sm:object-cover'}
                    />
                  ) : (
                    <Video src={asset.src} isFocused={isFocused} onlyLockAspectOnMobile className={'h-full'} />
                  )}
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
        <div
          className={classNames(
            isActionAreaShow ? 'sm:opacity-100' : 'sm:opacity-0',
            'absolute bottom-0 inset-x-0',
            'z-10',
            'py-4',
            'sm:bg-[#1C1E21] sm:bg-opacity-[40%]'
          )}
        >
          <p className={classNames('text-center', 'text-xs text-white')}>
            {innerIndex + 1} / {total}
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};
